import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWebsiteImages, slotBackground, useGiving, BANK_ROWS } from '../lib/publicContent';
import { ed, usePageMeta } from '../lib/editTexts';
import PublicNav from '../components/public/PublicNav';
import PublicFooter from '../components/public/PublicFooter';
import '../styles/public.css';

// Bankdaten und PayPal kommen aus AgoraHub (Inhalte → Geben) und sind dort zentral gepflegt (data-edit-central).

export default function Giving() {
  const { t, i18n } = useTranslation();
  const slots = useWebsiteImages();
  const giving = useGiving(i18n.language);
  usePageMeta('giving');
  const [copied, setCopied] = useState('');

  const copy = (text, key) => {
    try { navigator.clipboard?.writeText(text); } catch {}
    setCopied(key);
    setTimeout(() => setCopied(''), 2200);
  };
  // Vor der Antwort Platzhalter (kein Springen), danach nur Zeilen mit Wert.
  const rows = giving.ready ? BANK_ROWS.filter(row => giving.bank[row.field]) : BANK_ROWS;

  return (
    <div className="kac-public">
      <div className="kac-inner">
        <PublicNav />

        {/* HERO */}
        <div className="kac-hero">
          <div className="kp-kicker" style={{ marginBottom: 12 }} {...ed('giving.eyebrow')}>{t('giving.eyebrow')}</div>
          <h1 {...ed('giving.title')} style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 78, lineHeight: 0.95, margin: '0 0 24px' }}>
            {t('giving.title')}
          </h1>
          <p {...ed('giving.description')} style={{ fontSize: 19, lineHeight: 1.65, color: '#5e554b', maxWidth: 600, margin: 0 }}>
            {t('giving.description')}
          </p>
        </div>

        {/* IMAGE */}
        <div data-slot="page.giving" style={{ height: 340, ...slotBackground(slots, 'page.giving', 0, '/kac-assets/photos/DSC09748.jpg', 'center 38%'), backgroundSize: 'cover', borderRadius: 6, marginBottom: 0 }} />

        {/* METHODS */}
        <div style={{ padding: '56px 4px 24px' }}>
          <div className="kp-kicker" style={{ marginBottom: 8 }} {...ed('giving.methods_kicker')}>{t('giving.methods_kicker')}</div>
          <h2 {...ed('giving.methods_title')} style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 40, margin: '0 0 40px' }}>{t('giving.methods_title')}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '0.92fr 1.08fr', gap: 28 }}>

            {/* PayPal */}
            <div className="kac-red-panel" style={{ padding: '48px 44px', display: 'flex', flexDirection: 'column' }}>
              <div {...ed('giving.paypal.kicker')} style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.7, marginBottom: 16 }}>{t('giving.paypal.kicker')}</div>
              <h3 {...ed('giving.paypal.title')} style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 28, margin: '0 0 16px' }}>{t('giving.paypal.title')}</h3>
              <p {...ed('giving.paypal.description')} style={{ fontSize: 16, lineHeight: 1.65, opacity: 0.88, margin: '0 0 36px', flex: 1 }}>{t('giving.paypal.description')}</p>
              <a
                href={giving.paypalUrl || undefined}
                aria-disabled={giving.paypalUrl ? undefined : 'true'}
                data-edit-central="giving"
                target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-block', padding: '13px 26px', borderRadius: 999, background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff', fontWeight: 600, fontSize: 15, textDecoration: 'none', alignSelf: 'flex-start', transition: 'background .15s ease', opacity: giving.paypalUrl ? 1 : 0.6 }}
              >
                <span {...ed('giving.paypal.button')}>{t('giving.paypal.button')}</span> →
              </a>
            </div>

            {/* Bank */}
            <div className="kac-card" style={{ padding: '44px 44px' }}>
              <div {...ed('giving.bank.kicker')} style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#857A6E', marginBottom: 6 }}>{t('giving.bank.kicker')}</div>
              <div {...ed('giving.bank.copy_hint')} style={{ fontSize: 12, color: '#9b8f7f', marginBottom: 20 }}>{t('giving.bank.copy_hint')}</div>
              <h3 {...ed('giving.bank.title')} style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 26, margin: '0 0 28px' }}>{t('giving.bank.title')}</h3>
              {rows.map((row, i) => {
                const value = giving.bank[row.field] || '';
                return (
                  <button key={row.key} type="button" disabled={!value} onClick={() => copy(value, row.key)} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    width: '100%', textAlign: 'left', gap: 16, padding: '17px 0',
                    cursor: value ? 'pointer' : 'default', background: 'none', border: 'none',
                    borderBottom: i < rows.length - 1 ? '1px solid rgba(120,100,70,0.2)' : 'none',
                    fontFamily: "'Hanken Grotesk',sans-serif",
                  }}>
                    <div>
                      <div {...ed(`giving.bank.${row.key}`)} style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9b8f7f', marginBottom: 4 }}>{t(`giving.bank.${row.key}`)}</div>
                      <div data-edit-central="giving" style={{ fontSize: 16, fontWeight: 600, color: '#26211C', fontVariantNumeric: 'tabular-nums', minHeight: 22 }}>{value || '…'}</div>
                    </div>
                    <span {...ed(copied === row.key ? 'giving.bank.copied' : 'giving.bank.copy')} style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', color: copied === row.key ? '#1F8A5B' : '#B6AA98', whiteSpace: 'nowrap', transition: 'color .2s ease' }}>
                      {copied === row.key ? t('giving.bank.copied') : t('giving.bank.copy')}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <PublicFooter />
      </div>
    </div>
  );
}
